import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { css } from 'glamor';
import { useQuery, useMutation } from "@apollo/client";
import TablePagination from '@material-ui/core/TablePagination';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { Grid } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import GetAppIcon from '@material-ui/icons/GetApp';

import * as theme from '../../../theme';
import { Button } from '../../../components/Button';
import { CardContainer, Container } from '../../../components/Container';
import { FlashMessage } from '../../../components/FlashMessage';
import { Alert } from '../../../components/Alert';
import { H1 } from '../../../components/Typography';
import { Left, Right } from '../../../components/Header';
import { Loading, LoadingError } from '../../../components/LoadingFork';
import { Table, TBody, THead } from '../../../components/Table';
import { Well } from '../../../components/Well';
import { withInstallation } from '../../../components/InstallationContext';
import { Header } from '../RateCards/components/common';
import { getCurrentParkingInventory, markCarAsExited } from "./queries";
import { fmtDate } from "../../../lib/misc";
import { DRIVO_URL } from '../../../lib/drivoApollo';
import { downloadWithAuth } from '../../../lib/authManager';
import { DateTime } from 'luxon';
import { DateInput } from '../../../components/DateInput';
import { SelectInput } from '../../../components/SelectInput';
import { Input } from '../../../components/Input';

// Compact styling for the occupancy report date picker. The picker sits at the
// right edge of the page header, so float its calendar rightwards to keep it on screen.
const reportDatePicker = css({
  ' input': {
    height: '34px !important',
    fontSize: '13px !important',
    borderWidth: '1px !important',
    paddingRight: '34px !important',
  },
  ' img': {
    width: '14px',
    right: '10px',
  },
  ' .DayPicker': {
    left: 'auto !important',
    right: '0 !important',
    top: '38px !important',
  },
});

const useStyles = makeStyles(() => ({
  statsCard: {
    marginBottom: '20px',
    borderRadius: '14px',
    background: '#fff',
    boxShadow: '0 6px 10px rgba(0,0,0,.08), 0 0 6px rgba(0,0,0,.05)',
    transition: '.3s transform cubic-bezier(.155,1.105,.295,1.12),.3s box-shadow,.3s -webkit-transform cubic-bezier(.155,1.105,.295,1.12)',
    height: '100%',
  },
  statLabel: {
    fontSize: '14px',
    fontWeight: 500,
    opacity: 0.72,
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  statValue: {
    fontSize: '36px',
    fontWeight: 600,
    color: '#3127B8',
  },
  statSubtext: {
    fontSize: '12px',
    color: '#666',
    marginTop: '4px',
  },
}));

const rowStyle = css({
  '> td': {
    verticalAlign: 'middle',
    height: 60,
    borderBottom: `1px solid ${theme.gray10}`,
    transition: 'all .2s linear',
  },
  '> td:first-child': { paddingLeft: 10 },
  '> td:last-child': { paddingRight: 10 },
});

// Color scheme mapping for parking types
const parkingTypeColors = {
  Sanctioned: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32'
  },
  Booking: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2'
  },
  Casual: {
    backgroundColor: '#fff3e0',
    color: '#e65100'
  }
};

function getParkingTypeStyle(parkingType) {
  const type = parkingType || 'Casual';
  return parkingTypeColors[type] || parkingTypeColors.Casual;
}

/**
 * Max rows fetched in one call (a cap, not a guarantee). If totalCount
 * exceeds this, stats and filters are incomplete unless further pages
 * are fetched.
 */
const INVENTORY_FETCH_LIMIT = 10000;

function ParkingInventoryTable({ inventory, timezone, onMarkAsExited, facilityId }) {
  if (!inventory || inventory.length === 0) {
    return (
      <tr>
        <td colSpan={6}>
          <Well>
            <div>
              <H1 spaceBelow="large">No cars currently in car park</H1>
            </div>
          </Well>
        </td>
      </tr>
    );
  }

  return inventory.map((item, index) => {
    // Calculate hours in car park for warning
    const entryDateTime = DateTime.fromISO(item.entryTime);
    const now = DateTime.now().setZone(timezone);
    const hoursInPark = now.diff(entryDateTime, 'hours').hours;
    const daysInPark = hoursInPark / 24;

    // Show warning for cars parked > 24 hours
    const isLongStay = hoursInPark > 24;
    const isVeryLongStay = daysInPark > 7;

    const rowWarningStyle = (isLongStay || isVeryLongStay) ? {
      backgroundColor: isVeryLongStay ? '#fff3cd' : '#fff8e1',
      borderLeft: `4px solid ${isVeryLongStay ? '#ff9800' : '#ffc107'}`,
    } : {};

    return (
      <tr key={`${item.plate}-${index}`} className={rowStyle} style={rowWarningStyle}>
        <td style={{ padding: '8px 12px' }}>
          {item.plate}
          {isVeryLongStay && (
            <span style={{
              marginLeft: '8px',
              fontSize: '12px',
              color: '#ff9800',
              fontWeight: 'bold'
            }}>
              ⚠️ {Math.floor(daysInPark)} days
            </span>
          )}
          {isLongStay && !isVeryLongStay && (
            <span style={{
              marginLeft: '8px',
              fontSize: '12px',
              color: '#ffc107'
            }}>
              ⏱ {Math.floor(hoursInPark)}h
            </span>
          )}
        </td>
        <td style={{ fontSize: '12px', padding: '8px 12px' }}>{fmtDate(item.entryTime, timezone, 'd MMM yyyy HH:mm:ss')}</td>
        <td style={{ fontSize: '12px', padding: '8px 12px' }}>{item.entryGate}</td>
        <td style={{ fontSize: '12px', padding: '8px 12px' }}>{item.duration}</td>
        <td style={{ padding: '8px 12px' }}>
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            ...getParkingTypeStyle(item.parkingType)
          }}>
            {item.parkingType || 'Casual'}
          </span>
        </td>
        <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '8px 12px' }}>
          <Button
            onClick={() => onMarkAsExited(item.plate)}
            style={{
              fontSize: '11px',
              padding: '3px 10px',
              backgroundColor: theme.gray20,
              minWidth: 'auto',
              height: 'auto',
              lineHeight: 'normal'
            }}
          >
            Mark as Exited
          </Button>
        </td>
      </tr>
    );
  });
}

function ParkingInventory({ installation, facility, match, location }) {
  const classes = useStyles();
  // Check if we're in local/dev environment
  const isLocalEnv = DRIVO_URL && DRIVO_URL.includes('localhost');
  // Default filter: 'all' for local (to show old data), 'today' for production
  const [filterType, setFilterType] = useState(isLocalEnv ? 'all' : 'today'); // 'today', 'custom', or 'all'
  const [parkingTypeFilter, setParkingTypeFilter] = useState('all'); // 'all', 'Sanctioned', 'Booking', or 'Casual'
  const [searchPlate, setSearchPlate] = useState(''); // Search term for plate number
  // Default to today if after 1pm, otherwise default to yesterday
  const defaultDateTimezone = installation?.timeZoneName || 'Australia/Sydney';
  const defaultDateNow = DateTime.now().setZone(defaultDateTimezone);
  const isBefore1pm = defaultDateNow.hour < 13;
  const defaultDate = isBefore1pm
    ? defaultDateNow.minus({ days: 1 }).toISODate() // Yesterday if before 1pm
    : defaultDateNow.toISODate(); // Today if after 1pm
  const [selectedReportDate, setSelectedReportDate] = useState(defaultDate || '');
  const [page, setPage] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [customDate, setCustomDate] = useState('');
  const [markAsExitedDialog, setMarkAsExitedDialog] = useState({ open: false, plate: null });

  // Calculate entryDateFrom based on filter type
  const entryDateFrom = filterType === 'today'
    ? DateTime.now().setZone(installation.timeZoneName || 'Australia/Sydney').startOf('day').toISO()
    : filterType === 'custom' && customDate
      ? DateTime.fromISO(customDate).setZone(installation.timeZoneName || 'Australia/Sydney').startOf('day').toISO()
      : null; // 'all' or no custom date selected - show all cars

  // Fetch up to INVENTORY_FETCH_LIMIT rows once; the table is paginated client-side below
  const { data = {}, loading, error, refetch } = useQuery(getCurrentParkingInventory, {
    fetchPolicy: 'network-only',
    variables: {
      facilityId: facility?.id,
      first: INVENTORY_FETCH_LIMIT,
      skip: 0,
      entryDateFrom: entryDateFrom,
    },
    skip: !facility?.id,
  });

  const inventoryData = data.getCurrentParkingInventory || { items: [], totalCount: 0 };
  const allInventoryItems = inventoryData.items || [];

  // Check if we're in a development environment (for debug logging)
  const isDevelopment = process.env.NODE_ENV !== 'production' || window.location.hostname === 'localhost' || window.location.hostname.includes('staging');

  // Debug: Log parking types to see what we're getting (only in non-production environments)
  if (allInventoryItems.length > 0 && !loading && isDevelopment) {
    const parkingTypes = allInventoryItems.map(item => item.parkingType || 'undefined');
    const uniqueTypes = [...new Set(parkingTypes)];
    const sanctionedCount = allInventoryItems.filter(item => (item.parkingType || 'Casual') === 'Sanctioned').length;
    const bookingCount = allInventoryItems.filter(item => (item.parkingType || 'Casual') === 'Booking').length;
    const casualCount = allInventoryItems.filter(item => (item.parkingType || 'Casual') === 'Casual').length;
    console.log('[ParkingInventory] Parking types in data:', uniqueTypes);
    console.log('[ParkingInventory] Sanctioned count:', sanctionedCount, 'Booking count:', bookingCount, 'Casual count:', casualCount);
    console.log('[ParkingInventory] Sample items:', allInventoryItems.slice(0, 3).map(item => ({
      plate: item.plate,
      parkingType: item.parkingType || 'undefined'
    })));
  }

  // Filter by parking type
  let filteredInventory = parkingTypeFilter === 'all'
    ? allInventoryItems
    : allInventoryItems.filter(item => {
        const itemType = item.parkingType || 'Casual'; // Default to Casual if undefined
        const matches = itemType === parkingTypeFilter;
        if (!matches && parkingTypeFilter !== 'all' && isDevelopment) {
          // Additional debug for non-"all" parking type filters (only in non-production environments)
          console.log('[ParkingInventory] Filtering out:', item.plate, 'type:', itemType, 'expected:', parkingTypeFilter);
        }
        return matches;
      });

  // Filter by plate search term
  if (searchPlate && searchPlate.trim()) {
    const searchTerm = searchPlate.trim().toUpperCase();
    filteredInventory = filteredInventory.filter(item => {
      const plate = (item.plate || '').toUpperCase();
      return plate.includes(searchTerm);
    });
  }

  if (isDevelopment) {
    console.log('[ParkingInventory] Filter:', parkingTypeFilter, 'Search:', searchPlate, 'Filtered count:', filteredInventory.length, 'Total items:', allInventoryItems.length);
  }

  // Stats are computed over the full filtered list; only the table is paged
  const inventory = filteredInventory;
  const totalCount = filteredInventory.length;
  const pagedInventory = inventory.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  // Calculate stats for cards
  const statsTimezone = installation.timeZoneName || 'Australia/Sydney';
  const statsNow = DateTime.now().setZone(statsTimezone);

  // Count long-stay cars (>24 hours)
  const longStayCars = inventory.filter(item => {
    const entryTime = DateTime.fromISO(item.entryTime).setZone(statsTimezone);
    const duration = statsNow.diff(entryTime, 'hours');
    return duration.hours > 24;
  }).length;

  // Calculate average duration
  const avgDurationHours = inventory.length > 0
    ? inventory.reduce((sum, item) => {
        const entryTime = DateTime.fromISO(item.entryTime).setZone(statsTimezone);
        const duration = statsNow.diff(entryTime, 'hours');
        return sum + duration.hours;
      }, 0) / inventory.length
    : 0;
  const avgDurationStr = avgDurationHours >= 24
    ? `${Math.floor(avgDurationHours / 24)}d ${Math.floor(avgDurationHours % 24)}h`
    : avgDurationHours >= 1
      ? `${Math.floor(avgDurationHours)}h ${Math.floor((avgDurationHours % 1) * 60)}m`
      : `${Math.floor(avgDurationHours * 60)}m`;

  // Find oldest entry
  const oldestEntry = inventory.length > 0
    ? inventory.reduce((oldest, item) => {
        const entryTime = DateTime.fromISO(item.entryTime).setZone(statsTimezone);
        const oldestTime = oldest ? DateTime.fromISO(oldest.entryTime).setZone(statsTimezone) : null;
        return !oldestTime || entryTime < oldestTime ? item : oldest;
      }, null)
    : null;

  const oldestDuration = oldestEntry
    ? (() => {
        const entryTime = DateTime.fromISO(oldestEntry.entryTime).setZone(statsTimezone);
        const duration = statsNow.diff(entryTime, ['days', 'hours']);
        return duration.days > 0
          ? `${duration.days}d ${Math.floor(duration.hours)}h`
          : `${Math.floor(duration.hours)}h`;
      })()
    : 'N/A';

  const [markCarAsExitedMutation, { loading: markingAsExited }] = useMutation(markCarAsExited, {
    onCompleted: () => {
      setMarkAsExitedDialog({ open: false, plate: null });
      setErrorMessage(null); // Clear any previous errors
      refetch(); // Refresh the list
    },
    onError: (error) => {
      setErrorMessage(`Error marking car as exited: ${error.message}`);
      // Auto-dismiss error after 5 seconds
      setTimeout(() => setErrorMessage(null), 5000);
    },
  });

  async function handleGenerateOccupancyReport() {
    if (!facility?.id || !selectedReportDate) return;

    const reportTimezone = installation.timeZoneName || 'Australia/Sydney';
    const reportDate = DateTime.fromISO(selectedReportDate, { zone: reportTimezone }).startOf('day');
    const reportNow = DateTime.now().setZone(reportTimezone);
    const reportToday = reportNow.startOf('day');

    // Check if trying to download today's report before 1pm
    if (reportDate.hasSame(reportToday, 'day')) {
      const currentHour = reportNow.hour;
      if (currentHour < 13) {
        setErrorMessage(`Cannot download today's report before 1:00 PM local time. Current time: ${reportNow.toFormat('HH:mm')}. Please select a previous date or wait until after 1:00 PM.`);
        // Auto-dismiss error after 5 seconds
        setTimeout(() => setErrorMessage(null), 5000);
        return;
      }
    }

    const snapshotDate = reportDate.set({ hour: 13, minute: 0, second: 0 });
    const snapshotTimeISO = snapshotDate.toISO();

    await downloadWithAuth({
      url: `${DRIVO_URL}/parking-inventory/${installation.id}/download?snapshotTime=${encodeURIComponent(snapshotTimeISO || '')}`,
      filename: `occupancy-report_${reportDate.toFormat('yyyyMMdd')}_1300_${installation.brandingKey}.xlsx`,
    });
  }

  function handleReportDateSelect(date) {
    setSelectedReportDate(date || '');
  }

  // The report date is a plain YYYY-MM-DD string, always read in the facility's timezone
  const reportTimezone = installation?.timeZoneName || 'Australia/Sydney';
  const reportNow = DateTime.now().setZone(reportTimezone);
  const reportToday = reportNow.startOf('day');
  const reportTodayDate = reportToday.toISODate();

  // Any past date can be picked. Future dates have no occupancy to report on.
  // Compare calendar labels so the browser's own timezone can't shift the boundary by a day.
  const isReportDateOutsideRange = day => day.format('YYYY-MM-DD') > reportTodayDate;

  // Check if selected date is today and before 1pm
  const selectedReportDateObj = selectedReportDate
    ? DateTime.fromISO(selectedReportDate, { zone: reportTimezone }).startOf('day')
    : null;
  const isTodaySelected = selectedReportDateObj && selectedReportDateObj.hasSame(reportToday, 'day');
  const isBefore1pmForReport = reportNow.hour < 13;
  const canDownloadToday = !isTodaySelected || !isBefore1pmForReport;

  function handleChangePage(event, newPage) {
    setPage(newPage);
  }

  function handleChangeRowsPerPage(event) {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }

  function handleFilterTypeChange(value) {
    setFilterType(value);
    setPage(0); // Reset to first page when filter changes
    if (value === 'today' || value === 'all') {
      setCustomDate('');
    }
  }

  function handleParkingTypeFilterChange(value) {
    setParkingTypeFilter(value);
    setPage(0); // Reset to first page when filter changes
  }

  function handleSearchPlateChange(value) {
    setSearchPlate(value);
    setPage(0); // Reset to first page when search changes
  }

  function handleCustomDateChange(date) {
    setCustomDate(date);
    setPage(0); // Reset to first page when date changes
  }

  function handleMarkAsExited(plate) {
    setMarkAsExitedDialog({ open: true, plate });
  }

  function handleConfirmMarkAsExited() {
    if (!markAsExitedDialog.plate || !facility?.id) return;

    markCarAsExitedMutation({
      variables: {
        facilityId: facility.id,
        plate: markAsExitedDialog.plate,
        exitTime: DateTime.now().setZone(installation.timeZoneName || 'Australia/Sydney').toISO(),
      },
    });
  }

  function handleCancelMarkAsExited() {
    setMarkAsExitedDialog({ open: false, plate: null });
  }

  return (
    <div>
      <CardContainer>
        <Helmet>
          <title>Parking Inventory</title>
        </Helmet>
        <Container size="large" spaceAbove="small" spaceBelow="medium">
          <Header>
            <Left>
              <H1>Parking Inventory</H1>
            </Left>
            <Right>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div className={reportDatePicker} style={{ width: '200px' }}>
                  <DateInput
                    floatPicker
                    noMarginBottom
                    autoComplete="off"
                    placeholder="Select report date"
                    displayFormat="ddd, D MMM YYYY"
                    value={selectedReportDate}
                    onChange={handleReportDateSelect}
                    isOutsideRange={isReportDateOutsideRange}
                    disabled={!facility?.id || loading}
                  />
                </div>
                <Button
                  onClick={handleGenerateOccupancyReport}
                  style={{
                    backgroundColor: canDownloadToday ? '#ff9800' : '#ccc',
                    cursor: canDownloadToday ? 'pointer' : 'not-allowed',
                    color: 'white',
                    fontSize: '12px',
                    padding: '6px 12px',
                    minWidth: 'auto'
                  }}
                  disabled={!facility?.id || loading || !selectedReportDate || !canDownloadToday}
                  title={
                    !canDownloadToday
                      ? `Cannot download today's report before 1:00 PM local time (Current: ${reportNow.toFormat('HH:mm')})`
                      : "Download report for selected date"
                  }
                >
                  <GetAppIcon style={{ fontSize: '18px', marginRight: '4px' }} />
                  Download
                </Button>
              </div>
            </Right>
          </Header>
        </Container>

        {/* Date Filter */}
        <Container size="large" spaceBelow="medium">
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ minWidth: '200px' }}>
              <SelectInput
                label="Filter by Entry Date"
                value={filterType}
                onChange={(e) => handleFilterTypeChange(e.target.value)}
                options={[
                  { value: 'all', label: 'All Cars' },
                  { value: 'today', label: 'Today (since midnight)' },
                  { value: 'custom', label: 'Custom Date' },
                ]}
              />
            </div>
            <div style={{ minWidth: '200px' }}>
              <SelectInput
                label="Filter by Parking Type"
                value={parkingTypeFilter}
                onChange={(e) => handleParkingTypeFilterChange(e.target.value)}
                options={[
                  { value: 'all', label: 'All Types' },
                  { value: 'Sanctioned', label: 'Sanctioned' },
                  { value: 'Booking', label: 'Booking' },
                  { value: 'Casual', label: 'Casual' },
                ]}
              />
            </div>
            <div style={{ minWidth: '200px' }}>
              <Input
                label="Search Plate Number"
                placeholder="Enter plate number"
                value={searchPlate}
                onValueChange={handleSearchPlateChange}
              />
            </div>
            {filterType === 'custom' && (
              <div style={{ minWidth: '200px' }}>
                <DateInput
                  floatPicker
                  label="Entry Date From"
                  placeholder="Select date"
                  displayFormat="ddd, D MMM YYYY"
                  value={customDate}
                  onChange={handleCustomDateChange}
                  isOutsideRange={() => false}
                />
              </div>
            )}
            {filterType === 'today' && (
              <div style={{ fontSize: '14px', color: theme.gray20, paddingBottom: '16px' }}>
                Showing cars that entered since {DateTime.now()
                  .setZone(installation.timeZoneName || 'Australia/Sydney')
                  .startOf('day')
                  .toFormat('d MMM yyyy HH:mm')}
              </div>
            )}
            {filterType === 'all' && (
              <div style={{ fontSize: '14px', color: theme.gray20, paddingBottom: '16px' }}>
                Showing all cars currently in the car park (no date filter)
              </div>
            )}
          </div>
        </Container>

        {/* Stats Cards */}
        {!loading && (
          <Container size="large" spaceBelow="medium">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statsCard}>
                  <CardContent>
                    <Typography className={classes.statLabel}>
                      Total Cars in Car Park
                    </Typography>
                    <Typography className={classes.statValue}>
                      {totalCount}
                    </Typography>
                    <Typography className={classes.statSubtext}>
                      {parkingTypeFilter !== 'all'
                        ? `${parkingTypeFilter} parking only`
                        : filterType === 'all'
                          ? 'All currently parked vehicles'
                          : filterType === 'today'
                            ? 'Entered since midnight today'
                            : customDate
                              ? `Entered since ${DateTime.fromISO(customDate).setZone(installation.timeZoneName || 'Australia/Sydney').toFormat('d MMM yyyy')}`
                              : 'Currently parked vehicles'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statsCard}>
                  <CardContent>
                    <Typography className={classes.statLabel}>
                      Long Stay Cars
                    </Typography>
                    <Typography className={classes.statValue}>
                      {longStayCars}
                    </Typography>
                    <Typography className={classes.statSubtext}>
                      Parked for more than 24 hours
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statsCard}>
                  <CardContent>
                    <Typography className={classes.statLabel}>
                      Average Duration
                    </Typography>
                    <Typography className={classes.statValue} style={{ fontSize: '28px' }}>
                      {avgDurationStr}
                    </Typography>
                    <Typography className={classes.statSubtext}>
                      Average time parked
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statsCard}>
                  <CardContent>
                    <Typography className={classes.statLabel}>
                      Oldest Entry
                    </Typography>
                    <Typography className={classes.statValue} style={{ fontSize: '28px' }}>
                      {oldestDuration}
                    </Typography>
                    <Typography className={classes.statSubtext}>
                      Longest time in car park
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            <div style={{
              marginTop: '16px',
              fontSize: '12px',
              color: theme.gray20,
              textAlign: 'right'
            }}>
              Last updated: {DateTime.now()
                .setZone(installation.timeZoneName || 'Australia/Sydney')
                .toFormat('HH:mm:ss on d MMM yyyy')}
            </div>
          </Container>
        )}

        <Container spaceBelow="large">
          <FlashMessage locationState={location?.state} />
          {errorMessage && (
            <Alert flavour="danger" spaceBelow="small">
              {errorMessage}
            </Alert>
          )}
          {loading && !data.getCurrentParkingInventory ? (
            <Loading />
          ) : error ? (
            <LoadingError error={error} />
          ) : (
            <>
              <Table>
                <THead>
                  <tr>
                    <th>Plate Number</th>
                    <th>Entry Time</th>
                    <th>Entry Gate</th>
                    <th>Duration in Car Park</th>
                    <th>Parking Type</th>
                    <th>Actions</th>
                  </tr>
                </THead>
                <TBody>
                  <ParkingInventoryTable
                    inventory={pagedInventory}
                    timezone={installation.timeZoneName}
                    onMarkAsExited={handleMarkAsExited}
                    facilityId={facility?.id}
                  />
                </TBody>
              </Table>
              {totalCount > 0 && (
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[25, 50, 100]}
                />
              )}
            </>
          )}
        </Container>
      </CardContainer>

      {/* Mark as Exited Confirmation Dialog */}
      <Dialog
        open={markAsExitedDialog.open}
        onClose={handleCancelMarkAsExited}
        aria-labelledby="mark-exited-dialog-title"
        aria-describedby="mark-exited-dialog-description"
      >
        <DialogTitle id="mark-exited-dialog-title">
          Mark Car as Exited
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="mark-exited-dialog-description">
            Are you sure you want to mark plate <strong>{markAsExitedDialog.plate}</strong> as exited?
            This will create an exit observation and remove it from the current inventory.
            <br /><br />
            This action should only be used when:
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li>The exit plate was not read properly</li>
              <li>The exit observation was not created</li>
              <li>You have confirmed the car has actually exited</li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelMarkAsExited}
            disabled={markingAsExited}
            style={{ backgroundColor: theme.gray20 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmMarkAsExited}
            disabled={markingAsExited}
            style={{ backgroundColor: '#3127B8', color: 'white' }}
          >
            {markingAsExited ? 'Marking...' : 'Mark as Exited'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default withInstallation(ParkingInventory);
